import { create } from 'zustand';
import { authenticatedGet, authenticatedDelete, handleApiResponse } from '../../../utils/apiClient';
import type { AgentStep, TaskGraph } from '../components/AgentProgress';
import type { MessageBlock, ToolCallBlock } from '../types/blocks';

/**
 * Convert backend toolCalls array to AgentStep[] for historical message rendering.
 *
 * The backend returns toolCalls when `include_trace=true` is passed to the
 * messages endpoint. Each entry is either a `tool_use` (call start) or
 * `tool_result` (call completion). We pair them by `call_id` to produce
 * fully-resolved AgentStep objects that MessageAgentSteps can render
 * identically to steps produced during live SSE streaming.
 */
function convertToolCallsToSteps(toolCalls: any[]): AgentStep[] {
  const steps: AgentStep[] = [];
  const callMap = new Map<string, AgentStep>();

  for (const tc of toolCalls) {
    if (tc.type === 'tool_use') {
      const step: AgentStep = {
        id: tc.call_id || `trace-${steps.length}`,
        type: 'agent_tool_call',
        agentId: tc.agent || 'Void',
        displayName: tc.display_name || tc.tool_name,
        displayDescription: tc.display_name || tc.tool_name,
        status: 'running', // will be updated by matching tool_result
        depth: 0,
        detail: tc.args_summary || '',
        toolCallId: tc.call_id,
        timestamp: new Date(tc.timestamp),
      };
      steps.push(step);
      if (tc.call_id) callMap.set(tc.call_id, step);
    } else if (tc.type === 'tool_result') {
      const matchingStep = tc.call_id ? callMap.get(tc.call_id) : null;
      if (matchingStep) {
        matchingStep.status = tc.success ? 'complete' : 'error';
        matchingStep.durationMs = tc.duration_ms;
        if (tc.result_summary) {
          matchingStep.detail = tc.result_summary;
        }
      }
    }
  }

  // All steps from history should be marked complete (fallback for missing tool_result)
  for (const step of steps) {
    if (step.status === 'running') {
      step.status = 'complete';
    }
  }

  return steps;
}

/**
 * Chat message interface
 */
export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  completed: boolean;
  // Streaming flag for real-time content updates (v7.5.0) ⭐ NEW
  streaming?: boolean;
  // Agent execution steps embedded in message (v1.2.0)
  agentSteps?: AgentStep[];
  // Task plan checkboxes for Thinking section (v1.4.0)
  taskPlan?: TaskPlanItem[];
  // TaskGraph from unified orchestration (v4.0)
  taskGraph?: TaskGraph;
  // Thinking hint for displaying brief status (v4.1.0)
  thinkingHint?: string | null;
  // Total duration from orchestration_completed event (v4.2.0) ⭐ NEW
  totalDurationMs?: number;
  // Block data from v4.0 Block SSE events
  blocks?: MessageBlock[];
  [key: string]: any;
}

/**
 * TODO item interface for orchestration tracking (v1.3.0)
 */
export interface TodoItem {
  id: string;
  content: string;
  activeForm: string;
  status: 'pending' | 'in_progress' | 'completed';
  agent?: string;
  resultSummary?: string;
}

/**
 * Task plan item for message-embedded checkbox display (v1.4.0)
 * Displayed in the Thinking section as interactive checkboxes
 */
export interface TaskPlanItem {
  id: string;
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  originalIndex: number;
}

/**
 * TODO progress tracking (v1.3.0)
 */
export interface TodoProgress {
  total: number;
  completed: number;
  inProgress: number;
  percentage: number;
}

/**
 * Progress summary from notification system (v1.2 - AGENT_NOTIFICATION_SPEC.md)
 */
export interface ProgressSummary {
  completed_count: number;
  in_progress: string | null;
  recent_operations: ProgressOperation[];
}

export interface ProgressOperation {
  action: string;
  timestamp: string;
  status: 'running' | 'completed' | 'failed';
  duration_ms?: number;
}

/**
 * Agent progress state for visualization
 * @deprecated Use message-embedded agentSteps instead (v1.2.0)
 */
export interface AgentProgressState {
  steps: AgentStep[];
  isAgentWorking: boolean;
}

/**
 * Chat store state interface
 */
export interface ChatState {
  // State
  messages: ChatMessage[];
  currentSessionId: string | null;
  isLoading: boolean;
  isLoadingMessages: boolean;
  isLoadingHistory: boolean; // Track history loading vs streaming (instant scroll fix)
  error: string | null;
  loadingSessions: Map<string, boolean>;
  sessionMessagesCache: Map<string, ChatMessage[]>;
  // Agent progress state (v1.1.0) - deprecated, kept for compatibility
  agentProgress: AgentProgressState;
  // TODO list state (v1.3.0) - orchestration task tracking
  todos: TodoItem[];
  todoProgress: TodoProgress;
  // Progress summary state (v1.2 - AGENT_NOTIFICATION_SPEC.md)
  progressSummary: ProgressSummary | null;

  // Actions
  loadSessionMessages: (sessionId: string | null) => Promise<boolean>;
  setCurrentSessionId: (sessionId: string | null) => void;
  addMessage: (message: ChatMessage) => void;
  updateLastMessage: (updater: (msg: ChatMessage) => ChatMessage) => void;
  setMessages: (messages: ChatMessage[] | ((prevMessages: ChatMessage[]) => ChatMessage[])) => void;
  clearChat: () => void;
  clearSessionMessages: () => Promise<boolean>;
  clearError: () => void;
  hasMessages: () => boolean;
  getCurrentSessionId: () => string | null;
  setSessionLoading: (sessionId: string, isLoading: boolean) => void;
  isSessionLoading: (sessionId: string) => boolean;
  isCurrentSessionLoading: () => boolean;
  addMessageToSession: (sessionId: string, message: ChatMessage) => void;
  updateLastMessageInSession: (sessionId: string, updater: (msg: ChatMessage) => ChatMessage) => void;
  updateMessageIdInSession: (sessionId: string, oldId: string | number, newId: string) => void;
  getSessionMessages: (sessionId: string) => ChatMessage[];
  clearSessionCache: (sessionId: string) => void;
  startIdeaGuidance: (idea: any) => void;
  sendIdeaMessage: (ideaId: string, message: string, stage?: string) => Promise<void>;
  // Agent progress actions (v1.1.0) - deprecated
  addAgentStep: (step: AgentStep) => void;
  updateAgentStep: (agentId: string, updates: Partial<AgentStep>) => void;
  clearAgentProgress: () => void;
  setAgentWorking: (working: boolean) => void;
  // Message-embedded agent steps actions (v1.2.0)
  addAgentStepToMessage: (messageId: string | number, step: AgentStep) => void;
  updateAgentStepInMessage: (messageId: string | number, stepId: string, updates: Partial<AgentStep>) => void;
  getMessageAgentSteps: (messageId: string | number) => AgentStep[];
  // TODO list actions (v1.3.0)
  setTodos: (todos: TodoItem[]) => void;
  updateTodo: (todoId: string, updates: Partial<TodoItem>) => void;
  addTodo: (todo: TodoItem) => void;
  clearTodos: () => void;
  // Block SSE actions (v4.0 Block System)
  appendBlockToMessage: (messageId: string | number, block: MessageBlock) => void;
  updateBlockInMessage: (messageId: string | number, blockId: string, update: Partial<ToolCallBlock> & Record<string, any>) => void;
}

/**
 * Chat Store
 *
 * Manages the current active chat session state including:
 * - Loading and displaying messages from existing sessions
 * - Managing current chat state and session ID
 * - Coordinating with ChatInterface for seamless session switching
 */
const useChatStore = create<ChatState>((set, get) => ({
  // State
  messages: [],
  currentSessionId: null,
  isLoading: false,
  isLoadingMessages: false,
  isLoadingHistory: false, // Initialize history loading flag
  error: null,
  loadingSessions: new Map(),
  sessionMessagesCache: new Map(),
  // Agent progress state (v1.1.0)
  agentProgress: {
    steps: [],
    isAgentWorking: false,
  },
  // TODO list state (v1.3.0)
  todos: [],
  todoProgress: {
    total: 0,
    completed: 0,
    inProgress: 0,
    percentage: 0,
  },
  // Progress summary state (v1.2)
  progressSummary: null,

  // Actions

  /**
   * Load messages from a specific chat session
   * @param sessionId - The session ID to load messages from
   * @returns - True if load was successful
   */
  loadSessionMessages: async (sessionId) => {
    if (!sessionId) {
      set({ messages: [], currentSessionId: null });
      return true;
    }

    set({ isLoadingMessages: true, isLoadingHistory: true, error: null });

    try {
      const response = await authenticatedGet(`/api/chat/sessions/${sessionId}/messages?include_trace=true`);
      const data = await handleApiResponse(Promise.resolve(response));

      if (data && Array.isArray(data.messages)) {
        const messagesWithDates: ChatMessage[] = data.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
          completed: true,
          // v4.1: Restore TaskGraph from historical messages
          // Historical messages with TaskGraph should show in collapsed state
          taskGraph: msg.taskGraph ? {
            tasks: msg.taskGraph.tasks.map((task: any) => ({
              ...task,
              // Ensure status is normalized for display
              status: task.status || 'completed',
            })),
            strategy: msg.taskGraph.strategy || 'sequential',
          } : undefined,
          // v3.3: Convert toolCalls trace data to agentSteps for historical rendering
          agentSteps: msg.toolCalls && msg.toolCalls.length > 0
            ? convertToolCallsToSteps(msg.toolCalls)
            : undefined,
          // v4.0: Load blocks from messages table (dual-read enrichment)
          blocks: msg.blocks || undefined,
        }));

        const { sessionMessagesCache } = get();
        const cachedMessages = sessionMessagesCache.get(sessionId) || [];

        const allMessages = [...messagesWithDates, ...cachedMessages];

        set({
          messages: allMessages,
          currentSessionId: sessionId,
          isLoadingMessages: false,
          // Keep isLoadingHistory true temporarily to trigger instant scroll
          // Will be reset in next tick
          error: null
        });

        // Reset isLoadingHistory after a short delay to ensure scroll completes
        setTimeout(() => {
          set({ isLoadingHistory: false });
        }, 100);

        return true;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load session messages';
      set({
        isLoadingMessages: false,
        isLoadingHistory: false,
        error: errorMessage
      });
      return false;
    }
  },

  /**
   * Set the current session ID (for new sessions)
   * @param sessionId - The session ID
   */
  setCurrentSessionId: (sessionId) => {
    set({ currentSessionId: sessionId });
  },

  /**
   * Add a new message to the current chat
   * @param message - The message to add
   */
  addMessage: (message) => {
    set(state => ({
      messages: [...(Array.isArray(state.messages) ? state.messages : []), message]
    }));
  },

  /**
   * Update the last message (for streaming responses)
   * @param updater - Function to update the last message
   */
  updateLastMessage: (updater) => {
    set(state => {
      const safeMessages = Array.isArray(state.messages) ? state.messages : [];
      return {
        messages: safeMessages.map((msg, index) =>
          index === safeMessages.length - 1 ? updater(msg) : msg
        )
      };
    });
  },

  /**
   * Replace messages entirely (used by ChatInterface for local state management)
   * @param messages - New messages array or updater function
   */
  setMessages: (messages) => {
    if (typeof messages === 'function') {
      set(state => ({ messages: messages(Array.isArray(state.messages) ? state.messages : []) }));
    } else {
      set({ messages: Array.isArray(messages) ? messages : [] });
    }
  },

  /**
   * Clear the current chat (local only)
   */
  clearChat: () => {
    set({
      messages: [],
      currentSessionId: null,
      error: null
    });
  },

  /**
   * Clear messages from the current session (calls backend API)
   * @returns - True if clear was successful
   */
  clearSessionMessages: async () => {
    const { currentSessionId } = get();

    if (!currentSessionId) {
      set({ messages: [] });
      return true;
    }

    try {
      const response = await authenticatedDelete(`/api/chat/sessions/${currentSessionId}/messages`);

      const data = await handleApiResponse(Promise.resolve(response));

      if (data) {
        set({ messages: [] });
        return true;
      }

      return false;
    } catch (error: any) {
      set({ error: error.message || 'Failed to clear session messages' });
      return false;
    }
  },

  /**
   * Clear any existing error
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Check if the current chat has any messages
   * @returns - True if chat has messages
   */
  hasMessages: () => {
    const { messages } = get();
    return Array.isArray(messages) && messages.length > 0;
  },

  /**
   * Get the current session ID
   * @returns - Current session ID or null
   */
  getCurrentSessionId: () => {
    return get().currentSessionId;
  },

  /**
   * Set loading state for a specific session
   * @param sessionId - The session ID
   * @param isLoading - Loading state
   */
  setSessionLoading: (sessionId, isLoading) => {
    set(state => {
      const newLoadingSessions = new Map(state.loadingSessions);
      if (isLoading) {
        newLoadingSessions.set(sessionId, true);
      } else {
        newLoadingSessions.delete(sessionId);
      }
      return { loadingSessions: newLoadingSessions };
    });
  },

  /**
   * Check if a specific session is loading
   * @param sessionId - The session ID to check
   * @returns - True if session is loading
   */
  isSessionLoading: (sessionId) => {
    const { loadingSessions } = get();
    return loadingSessions.has(sessionId) && loadingSessions.get(sessionId) === true;
  },

  /**
   * Get loading state for the current session
   * CRITICAL FIX: Allow checking loading state for null session ID (new chats)
   * This ensures thinking indicator shows immediately when starting a new chat
   * @returns - True if current session is loading
   */
  isCurrentSessionLoading: () => {
    const { currentSessionId, loadingSessions } = get();
    // CRITICAL: Check loading state even when currentSessionId is null (for new chats)
    // This fixes the bug where thinking indicator doesn't start on first message
    return loadingSessions.has(currentSessionId) && loadingSessions.get(currentSessionId) === true;
  },

  /**
   * Add a message to a specific session's cache
   * Used for storing messages to background sessions
   * @param sessionId - The session ID
   * @param message - The message to add
   */
  addMessageToSession: (sessionId, message) => {
    set(state => {
      const newCache = new Map(state.sessionMessagesCache);
      const sessionMessages = newCache.get(sessionId) || [];
      newCache.set(sessionId, [...sessionMessages, message]);
      return { sessionMessagesCache: newCache };
    });
  },

  /**
   * Update the last message in a specific session's cache
   * @param sessionId - The session ID
   * @param updater - Function to update the message
   */
  updateLastMessageInSession: (sessionId, updater) => {
    set(state => {
      const newCache = new Map(state.sessionMessagesCache);
      const sessionMessages = newCache.get(sessionId) || [];
      if (sessionMessages.length > 0) {
        const updatedMessages = sessionMessages.map((msg, index) =>
          index === sessionMessages.length - 1 ? updater(msg) : msg
        );
        newCache.set(sessionId, updatedMessages);
      }
      return { sessionMessagesCache: newCache };
    });
  },

  /**
   * Get cached messages for a specific session
   * @param sessionId - The session ID
   * @returns - Cached messages for the session
   */
  getSessionMessages: (sessionId) => {
    const { sessionMessagesCache } = get();
    return sessionMessagesCache.get(sessionId) || [];
  },

  /**
   * Update a message ID in cached session messages (for regenerate fix)
   * 🔧 FIX (2025-12-22): Backend returns persisted UUID after message save
   * Frontend needs to update temporary ID to match backend for regenerate to work
   * @param sessionId - The session ID
   * @param oldId - The temporary message ID (Date.now() + Math.random())
   * @param newId - The persisted message ID (UUID from backend)
   */
  updateMessageIdInSession: (sessionId, oldId, newId) => {
    set(state => {
      const newCache = new Map(state.sessionMessagesCache);
      const sessionMessages = newCache.get(sessionId) || [];
      const updatedMessages = sessionMessages.map(msg =>
        msg.id === oldId ? { ...msg, id: newId } : msg
      );
      newCache.set(sessionId, updatedMessages);
      return { sessionMessagesCache: newCache };
    });
  },

  /**
   * Clear cached messages for a specific session
   * @param sessionId - The session ID
   */
  clearSessionCache: (sessionId) => {
    set(state => {
      const newCache = new Map(state.sessionMessagesCache);
      newCache.delete(sessionId);
      return { sessionMessagesCache: newCache };
    });
  },

  /**
   * Start idea guidance conversation
   * Sends initial AI message with guidance text for the idea
   * @param idea - The idea object that was just created
   */
  startIdeaGuidance: (idea) => {
    const guidanceMessage: ChatMessage = {
      id: `guidance-${Date.now()}`,
      text: `很好！你已经捕获了闪念："${idea.title}"。\n\n让我们一起深入探索这个想法。我会通过一些问题来帮助你完善它。\n\n你可以考虑以下问题：\n- 这个想法要解决什么具体问题？\n- 目标用户是谁？\n- 与现有解决方案相比，有什么独特之处？\n- 最核心的功能是什么？`,
      sender: 'bot',
      timestamp: new Date(),
      completed: true
    };

    set(state => ({
      messages: [...(Array.isArray(state.messages) ? state.messages : []), guidanceMessage]
    }));
  },

  /**
   * Send a message in idea refinement mode
   * This should be used instead of regular chat when in idea context
   * @param ideaId - The ID of the idea being refined
   * @param message - The user's message
   * @param stage - Current refinement stage
   */
  sendIdeaMessage: async (ideaId, message, stage = 'exploring') => {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: message,
      sender: 'user',
      timestamp: new Date(),
      completed: true
    };

    set(state => ({
      messages: [...(Array.isArray(state.messages) ? state.messages : []), userMessage]
    }));
  },

  // ============= Agent Progress Actions (v1.1.0) =============

  /**
   * Add a new agent step to the progress timeline
   * @param step - The agent step to add
   */
  addAgentStep: (step) => {
    // v4.3.7: Lightweight logging - detailed tracking in ChatInterface SSE handlers
    if (step.type === 'agent_delegating') {
    }

    set(state => ({
      agentProgress: {
        ...state.agentProgress,
        steps: [...state.agentProgress.steps, step],
        isAgentWorking: true,
      }
    }));
  },

  /**
   * Update an existing agent step by agentId
   * @param agentId - The agent ID to update
   * @param updates - Partial step updates
   */
  updateAgentStep: (agentId, updates) => {
    set(state => ({
      agentProgress: {
        ...state.agentProgress,
        steps: state.agentProgress.steps.map(step =>
          step.agentId === agentId
            ? { ...step, ...updates }
            : step
        ),
      }
    }));
  },

  /**
   * Clear all agent progress steps
   * Called when streaming completes or errors
   */
  clearAgentProgress: () => {
    set(state => ({
      agentProgress: {
        steps: [],
        isAgentWorking: false,
      }
    }));
  },

  /**
   * Set agent working state
   * @param working - Whether an agent is actively working
   */
  setAgentWorking: (working) => {
    set(state => ({
      agentProgress: {
        ...state.agentProgress,
        isAgentWorking: working,
      }
    }));
  },

  // ============= Message-Embedded Agent Steps Actions (v1.2.0) =============

  /**
   * Add an agent step to a specific message
   * @param messageId - The message ID to add the step to
   * @param step - The agent step to add
   *
   * FIX: Added deduplication to prevent duplicate steps with the same id or stepId
   */
  addAgentStepToMessage: (messageId, step) => {
    set(state => ({
      messages: state.messages.map(msg => {
        if (msg.id === messageId) {
          const existingSteps = msg.agentSteps || [];

          // Check if a step with the same id or stepId already exists
          const isDuplicate = existingSteps.some(
            existingStep =>
              existingStep.id === step.id ||
              (step.stepId && existingStep.stepId === step.stepId)
          );

          if (isDuplicate) {
            console.warn(
              '[chatStore] Attempted to add duplicate step:',
              { stepId: step.stepId, id: step.id, type: step.type, agentId: step.agentId },
              'Skipping duplicate.'
            );
            return msg; // Don't add duplicate
          }

          return { ...msg, agentSteps: [...existingSteps, step] };
        }
        return msg;
      })
    }));
  },

  /**
   * Update an agent step within a specific message
   * @param messageId - The message ID containing the step
   * @param stepId - The step ID to update
   * @param updates - Partial updates to apply
   */
  updateAgentStepInMessage: (messageId, stepId, updates) => {
    set(state => ({
      messages: state.messages.map(msg =>
        msg.id === messageId && msg.agentSteps
          ? {
              ...msg,
              agentSteps: msg.agentSteps.map(step =>
                step.id === stepId ? { ...step, ...updates } : step
              )
            }
          : msg
      )
    }));
  },

  /**
   * Get agent steps for a specific message
   * @param messageId - The message ID
   * @returns Array of agent steps or empty array
   */
  getMessageAgentSteps: (messageId) => {
    const { messages } = get();
    const message = messages.find(m => m.id === messageId);
    return message?.agentSteps || [];
  },

  // ============= TODO List Actions (v1.3.0) =============

  /**
   * Set the complete TODO list (from todo_list_created event)
   * @param todos - Array of TODO items
   */
  setTodos: (todos) => {
    const completed = todos.filter(t => t.status === 'completed').length;
    const inProgress = todos.filter(t => t.status === 'in_progress').length;
    const total = todos.length;

    set({
      todos,
      todoProgress: {
        total,
        completed,
        inProgress,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
      },
    });
  },

  /**
   * Update a specific TODO item (from todo_started or todo_completed events)
   * @param todoId - The TODO ID to update
   * @param updates - Partial updates to apply
   */
  updateTodo: (todoId, updates) => {
    set(state => {
      const updatedTodos = state.todos.map(todo =>
        todo.id === todoId ? { ...todo, ...updates } : todo
      );

      const completed = updatedTodos.filter(t => t.status === 'completed').length;
      const inProgress = updatedTodos.filter(t => t.status === 'in_progress').length;
      const total = updatedTodos.length;

      return {
        todos: updatedTodos,
        todoProgress: {
          total,
          completed,
          inProgress,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        },
      };
    });
  },

  /**
   * Add a new TODO item dynamically (from todo_added event)
   * @param todo - The TODO item to add
   */
  addTodo: (todo) => {
    set(state => {
      const newTodos = [...state.todos, todo];
      const completed = newTodos.filter(t => t.status === 'completed').length;
      const inProgress = newTodos.filter(t => t.status === 'in_progress').length;
      const total = newTodos.length;

      return {
        todos: newTodos,
        todoProgress: {
          total,
          completed,
          inProgress,
          percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        },
      };
    });
  },

  /**
   * Clear all TODOs (when orchestration completes or session changes)
   */
  clearTodos: () => {
    set({
      todos: [],
      todoProgress: {
        total: 0,
        completed: 0,
        inProgress: 0,
        percentage: 0,
      },
    });
  },

  // ============= Block SSE Actions (v4.0 Block System) =============

  /**
   * Append a new block to a message's blocks array.
   * Called when receiving a 'block' SSE event.
   * @param messageId - The message ID to append the block to
   * @param block - The MessageBlock to append
   */
  appendBlockToMessage: (messageId, block) => {
    set(state => ({
      messages: state.messages.map(msg => {
        if (msg.id === messageId) {
          const existingBlocks = msg.blocks || [];
          return { ...msg, blocks: [...existingBlocks, block] };
        }
        return msg;
      })
    }));
  },

  /**
   * Update an existing block within a message (identified by block.id).
   * Called when receiving a 'block_update' SSE event.
   * Only applies to tool_call blocks which have unique IDs.
   * @param messageId - The message ID containing the block
   * @param blockId - The block's unique ID (tool_call id)
   * @param update - Partial updates to merge into the block
   */
  updateBlockInMessage: (messageId, blockId, update) => {
    set(state => ({
      messages: state.messages.map(msg => {
        if (msg.id === messageId && msg.blocks) {
          return {
            ...msg,
            blocks: msg.blocks.map(block => {
              // Match tool_call blocks by id
              if (block.type === 'tool_call' && block.id === blockId) {
                return { ...block, ...update };
              }
              // Match task_plan blocks by id (for task status updates via block_update)
              if (block.type === 'task_plan' && (block as any).id === blockId && (update as any).tasks) {
                const existingTasks = (block as any).tasks || [];
                const incomingTasks = (update as any).tasks || [];
                // Update existing tasks
                const updatedTasks = existingTasks.map((task: any) => {
                  const taskUpdate = incomingTasks.find((u: any) => u.id === task.id);
                  return taskUpdate ? { ...task, ...taskUpdate } : task;
                });
                // Append new tasks (e.g. synthesis) not in the original plan
                const existingIds = new Set(existingTasks.map((t: any) => t.id));
                const newTasks = incomingTasks.filter((t: any) => !existingIds.has(t.id));
                return { ...block, tasks: [...updatedTasks, ...newTasks] };
              }
              return block;
            }),
          };
        }
        return msg;
      })
    }));
  },
}));

export default useChatStore;
